import { Component, Input } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { BaseServices } from '../../kuba.services';
import { FlexiOnboardingService } from './flexi-onboarding.service';
import {
  FlexiOnboardingFinalAction,
  FlexiOnboardingFinalStep,
  FlexiOnboardingMeeting,
  FlexiOnboardingResponsibleParty
} from './flexi-onboarding.models';

@Component({
  selector: 'flexi-onboarding-final-view',
  templateUrl: './onboarding-final-view.component.html',
  styleUrls: ['./flexi-onboarding.scss'],
  standalone: false
})
export class OnboardingFinalViewComponent {
  @Input() steps: FlexiOnboardingFinalStep[] = [];
  /** Cover-page metadata block — business, advisor, completion date, validity. */
  @Input() meeting: FlexiOnboardingMeeting | null = null;
  /** Agreed-actions appendix (aggregated public Action-type comments). */
  @Input() actions: FlexiOnboardingFinalAction[] = [];
  /** True once the CSM has called Complete and the steps have non-null FinalText. */
  @Input() isCompleted = false;

  /** Template bindings for the BE enum so the HTML can match against them. */
  readonly Responsible = FlexiOnboardingResponsibleParty;

  /** Latch toggled while the BE download is in flight to disable the button. */
  downloading = false;
  /** Localised error surfaced inline if the download fails. */
  errorMessage: string | null = null;

  constructor(
    private translate: TranslateService,
    private service: FlexiOnboardingService
  ) {}

  /** Localized label for the responsible-party chip on each action. */
  responsibleLabel(party: FlexiOnboardingResponsibleParty | null): string {
    const key = party === FlexiOnboardingResponsibleParty.Advisor
      ? 'FLEXI_ONBOARDING.RESPONSIBLE.ADVISOR'
      : 'FLEXI_ONBOARDING.RESPONSIBLE.CUSTOMER';
    return this.translate.instant(key);
  }

  /**
   * Downloads the same archived PDF the CSM sees and that lives in
   * Flexi / Documents / OHS-plans. Server-rendered so there is exactly one
   * source of truth for the customer-facing BHT-oppfølging report — the FE
   * no longer rebuilds the layout in jsPDF.
   *
   * Endpoint: GET /api/kuba/v1/flexi/onboarding/{businessId}/ohs-agreement.pdf.
   * It returns the binary blob stored in the OHS-plans folder; the file is
   * regenerated whenever the CSM completes (or re-completes) the onboarding.
   */
  downloadFinalViewPdf(): void {
    if (!this.isCompleted || this.downloading) return;
    const businessId = this.meeting?.businessId ?? BaseServices.BusinessId;
    if (!businessId) return;

    this.downloading = true;
    this.errorMessage = null;

    this.service.downloadOhsAgreementPdf(businessId).subscribe({
      next: (blob: Blob) => {
        // Mirror the BE FileName label so the saved file looks the same
        // whether the customer downloads it from here or the CSM grabs it
        // from the wizard / Flexi Documents tree.
        const baseName = this.translate.instant('FLEXI_ONBOARDING.PDF.FILE_NAME');
        const businessName = (BaseServices.BusinessName || `business-${businessId}`).trim();
        const safeBusinessName = businessName.replace(/[^a-zA-Z0-9-_ ]/g, '').trim() || 'business';

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${baseName}_${safeBusinessName}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);

        this.downloading = false;
      },
      error: (err: HttpErrorResponse) => {
        this.downloading = false;
        this.errorMessage = err.error?.detail
          ?? this.translate.instant('FLEXI_ONBOARDING.ERRORS.PDF_FAILED');
      }
    });
  }
}
